import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useDashboard } from './Dashboard';
import { getDashboardNavigatePath, parsePageParams } from 'components/resources/dashboards/DashboardNavigationUtils';
import { parseTemplateQueryInfo } from 'components/resources/dashboards/DashboardDocument';

export interface TemplateQueryInfo {
  templateQueryIds?: Array<string>;
  globalQueryId?: string;
  selectedTemplateQueryIndices?: Array<number>;
}

export default function DashboardTemplateQueryPage(): JSX.Element {
  const { queryIds, templateQueryIndices } = useParams();
  const dashboard = useDashboard();
  const navigate = useNavigate();

  // If this is a template dashboard and no template query index is provided,
  // or if the index is out of bounds, we need to redirect to the first template query.
  const templateQueryInfo = parseTemplateQueryInfo(queryIds, templateQueryIndices, dashboard);
  const indexValues = templateQueryInfo.selectedTemplateQueryIndices;
  const areValidIndices = indexValues !== undefined && indexValues.length > 0 && indexValues.every(index => index >= 0 && index < (templateQueryInfo.templateQueryIds?.length || 0));

  // In a useeffect because the react router doesn't like navigate calls while building a component.
  useEffect(() => {
    // If there are no provided template query IDs, or this is not a template dashboard,
    // we should redirect to the non-template path even if this is a template dashboard,
    // since it will trigger the default template query and provide a cleaner URL
    if (templateQueryInfo.templateQueryIds === undefined || templateQueryInfo.templateQueryIds.length === 0 || !dashboard.template_type) {
      const pathParts = parsePageParams();
      pathParts.templateQueryIndices = undefined;
      const navigatePath = getDashboardNavigatePath(pathParts);
      navigate(navigatePath, { replace: true });
    // Redirect to the first template query if we have queries but no valid index
    } else if (dashboard.template_type && !templateQueryInfo.templateQueryIds.length && !areValidIndices) {
      const pathParts = parsePageParams();
      pathParts.templateQueryIndices = [0];
      const navigatePath = getDashboardNavigatePath(pathParts);
      navigate(navigatePath, { replace: true });
    }
  }, [dashboard.template_type, templateQueryInfo.templateQueryIds, areValidIndices]);

  return (
    <Outlet />
  );
}